import {
    AfterViewChecked,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subject, fromEvent } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

import { ChatActivity, ChatMessage } from '../../../types/chat-session.types';
import { IChatTemplate } from '../../../types/chat-template.types';
import { ChatPageScrollEdge, getScrollTopForEdge } from '../../../utils/scroll-edge';
import { ChatMessageComponent } from '../chat-message/chat-message.component';
import { ChatActivityComponent } from '../chat-activity/chat-activity.component';

@Component({
    selector: 'chat-message-list',
    standalone: true,
    imports: [CommonModule, ChatMessageComponent, ChatActivityComponent],
    templateUrl: './chat-message-list.component.html',
    styleUrls: ['./chat-message-list.component.scss'],
})
export class ChatMessageListComponent implements OnInit, OnChanges, AfterViewChecked, OnDestroy {
    @Input() public messages: ChatMessage[] = [];
    @Input() public template!: IChatTemplate;
    @Input() public hasMoreBefore = false;
    @Input() public hasMoreAfter = false;
    @Input() public firstUnreadId: string | null = null;
    @Input() public activity: ChatActivity | null = null;
    @Input() public restoreScrollTop: number | null = null;
    @Input() public isLoadingBefore = false;
    @Input() public isLoadingAfter = false;

    @Output() public loadMoreBefore = new EventEmitter<void>();
    @Output() public loadMoreAfter = new EventEmitter<void>();
    @Output() public messageRead = new EventEmitter<string>();
    @Output() public scrollPositionChange = new EventEmitter<number>();

    private readonly destroy$ = new Subject<void>();
    private resizeObserver?: ResizeObserver;
    private restoreTarget: number | null = null;
    private pageScrollEdge: ChatPageScrollEdge | null = null;
    private lastFirstMessageId: string | null | undefined = undefined;
    private suppressScrollEmit = false;
    private settleTimer: ReturnType<typeof setTimeout> | null = null;

    public constructor(private readonly host: ElementRef<HTMLElement>) {}

    public ngOnInit(): void {
        this.restoreTarget = this.normalizeScrollTop(this.restoreScrollTop);

        fromEvent(this.host.nativeElement, 'scroll')
            .pipe(debounceTime(100), takeUntil(this.destroy$))
            .subscribe(() => {
                if (this.suppressScrollEmit) {
                    return;
                }
                this.clearRestoreTarget();
                this.emitScrollPosition();
            });

        if (typeof ResizeObserver !== 'undefined') {
            this.resizeObserver = new ResizeObserver(() => {
                this.tryApplyRestore();
                if (this.restoreTarget === null) {
                    this.emitScrollPosition();
                }
            });
            this.resizeObserver.observe(this.host.nativeElement);
        }
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes['restoreScrollTop'] && this.restoreTarget === null) {
            this.restoreTarget = this.normalizeScrollTop(this.restoreScrollTop);
        }

        if (changes['messages']) {
            const firstId = this.messages[0]?.id ?? null;
            const isPageReplace = this.lastFirstMessageId !== undefined
                && this.lastFirstMessageId !== null
                && firstId !== this.lastFirstMessageId;

            if (isPageReplace) {
                this.clearRestoreTarget(false);
            }

            this.lastFirstMessageId = firstId;

            if (this.restoreTarget !== null || this.pageScrollEdge) {
                queueMicrotask(() => this.tryApplyRestore());
            }
        }
    }

    public onLoadMoreBefore(): void {
        this.pageScrollEdge = 'end';
        this.loadMoreBefore.emit();
    }

    public onLoadMoreAfter(): void {
        this.pageScrollEdge = 'start';
        this.loadMoreAfter.emit();
    }

    public ngAfterViewChecked(): void {
        this.tryApplyRestore();
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.resizeObserver?.disconnect();
        this.clearSettleTimer();
    }

    public trackByMessageId(_index: number, message: ChatMessage): string {
        return message.id;
    }

    private tryApplyRestore(): void {
        const el = this.host.nativeElement;

        if (this.pageScrollEdge) {
            this.restoreTarget = getScrollTopForEdge(el, this.pageScrollEdge);
        }

        if (this.restoreTarget === null) {
            return;
        }

        if (el.scrollHeight <= el.clientHeight) {
            if (this.messages.length > 0) {
                this.scheduleSettle();
            }
            return;
        }

        const nextScrollTop = this.clampScrollTop(el, this.restoreTarget);

        if (Math.abs(el.scrollTop - nextScrollTop) > 1) {
            this.suppressScrollEmit = true;
            el.scrollTop = nextScrollTop;
            this.suppressScrollEmit = false;
        }

        if (this.canReachScrollTop(el, this.restoreTarget)) {
            this.emitScrollPosition();
            this.clearRestoreTarget();
            return;
        }

        this.scheduleSettle();
    }

    private scheduleSettle(): void {
        this.clearSettleTimer();
        this.settleTimer = setTimeout(() => {
            if (this.restoreTarget === null) {
                return;
            }
            this.emitScrollPosition();
            this.clearRestoreTarget();
        }, 300);
    }

    private clearRestoreTarget(clearPageScroll = true): void {
        this.restoreTarget = null;
        if (clearPageScroll) {
            this.pageScrollEdge = null;
        }
        this.clearSettleTimer();
    }

    private clearSettleTimer(): void {
        if (this.settleTimer !== null) {
            clearTimeout(this.settleTimer);
            this.settleTimer = null;
        }
    }

    private emitScrollPosition(): void {
        this.scrollPositionChange.emit(this.host.nativeElement.scrollTop);
    }

    private clampScrollTop(el: HTMLElement, value: number): number {
        const maxDistance = Math.max(0, el.scrollHeight - el.clientHeight);
        if (value < 0) {
            return Math.max(-maxDistance, Math.min(0, value));
        }
        return Math.max(0, Math.min(maxDistance, value));
    }

    private canReachScrollTop(el: HTMLElement, value: number): boolean {
        const maxDistance = Math.max(0, el.scrollHeight - el.clientHeight);
        if (value < 0) {
            return value >= -maxDistance;
        }
        return value <= maxDistance;
    }

    private normalizeScrollTop(value: number | null | undefined): number | null {
        return typeof value === 'number' && Number.isFinite(value) ? value : null;
    }
}
